import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * CarpoolTrustCard — LIVE list of drivers from /api/carpool/nearby.
 * Polylines follow real OSM road geometry (OSRM). Trust primitives are
 * named explicitly:
 *   * KYC via DigiLocker / mAadhaar — real integration surface named,
 *     not simulated Aadhaar hashing.
 *   * Trust score aggregated across rides completed.
 *   * Surge capped at ×1.5 by policy — never gouge a stranded rider.
 *   * SOS = one-tap escalate to the operator incident channel.
 * Beckn wire coming-soon once a Mumbai/Delhi BPP is live.
 */
import { useMemo, useState } from "react";
import { MockPill } from "../common/MockPill";
import { useCarpool } from "../../hooks/useCarpool";
const cardStyle = {
    padding: "16px 20px 20px 20px",
    display: "flex",
    flexDirection: "column",
    gap: 12,
};
const policyRow = {
    display: "flex",
    gap: 6,
    fontSize: 11,
    color: "var(--color-cool-ash)",
    flexWrap: "wrap",
};
const driverCard = {
    padding: "10px 12px",
    border: "1px solid var(--color-pebble)",
    borderRadius: 4,
    display: "flex",
    flexDirection: "column",
    gap: 6,
};
const rowFlex = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
};
const labelStyle = {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "var(--color-cool-ash)",
};
const monoBadge = {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: 10,
    padding: "2px 6px",
    border: "1px solid var(--color-pebble)",
    borderRadius: 3,
    background: "#f6f5f2",
};
const bookBtn = {
    padding: "8px 12px",
    background: "#000d10",
    color: "#f6e6df",
    border: "1px solid #000d10",
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    cursor: "pointer",
    whiteSpace: "nowrap",
};
const bookedBtn = {
    ...bookBtn,
    background: "#e6ebe4",
    color: "#5b6f5e",
    borderColor: "#c0d1c3",
    cursor: "default",
};
const sosBtn = {
    padding: "10px 14px",
    background: "transparent",
    color: "#7a2a1c",
    border: "1px solid #e5c3b3",
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    cursor: "pointer",
};
export function CarpoolTrustCard({ anchorLat, anchorLng }) {
    const { data, loading, error, book, refetch } = useCarpool({
        lat: anchorLat,
        lng: anchorLng,
        count: 6,
    });
    const [confirmation, setConfirmation] = useState(null);
    const top3 = useMemo(() => (data ? data.drivers.slice(0, 3) : []), [data]);
    const handleBook = async (driverId) => {
        const res = await book(driverId);
        if (res)
            setConfirmation(res.ride_id);
    };
    return (_jsxs("div", { style: cardStyle, "data-testid": "carpool-trust-card", children: [_jsxs("div", { style: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }, children: [_jsx(MockPill, { kind: "live", label: "OSRM ROUTED" }), _jsx(MockPill, { kind: "simulated", label: "DRIVER POOL" }), _jsx(MockPill, { kind: "coming-soon", label: "BECKN BPP" })] }), _jsxs("div", { style: policyRow, children: [_jsxs("span", { children: ["Surge cap \u00D7", data?.policy.max_surge ?? 1.5] }), _jsx("span", { children: "\u00B7" }), _jsxs("span", { children: ["Base \u20B9", data?.policy.base_fare_inr ?? 40] }), _jsx("span", { children: "\u00B7" }), _jsxs("span", { children: ["Per km \u20B9", data?.policy.per_km_inr ?? 12] }), loading ? (_jsxs(_Fragment, { children: [_jsx("span", { children: "\u00B7" }), _jsx("span", { children: "fetching OSRM\u2026" })] })) : null] }), error ? (_jsxs("div", { style: {
                    fontSize: 12,
                    padding: "8px 10px",
                    background: "#f6e6df",
                    border: "1px solid #e5c3b3",
                    color: "#7a4a38",
                    borderRadius: 4,
                }, children: [error, " \u2014 ", _jsx("button", { onClick: refetch, children: "retry" })] })) : null, top3.map((d) => (_jsxs("div", { style: driverCard, children: [_jsxs("div", { style: rowFlex, children: [_jsxs("div", { children: [_jsxs("div", { style: { fontSize: 14, fontWeight: 700 }, children: [d.name, " ", _jsxs("span", { style: { color: "var(--color-cool-ash)", fontWeight: 500 }, children: ["\u00B7 \u2605", d.trust_score.toFixed(2)] })] }), _jsx("div", { style: { fontSize: 11, color: "var(--color-cool-ash)" }, children: d.vehicle })] }), _jsx(MockPill, { kind: "simulated", label: "KYC" })] }), _jsxs("div", { style: rowFlex, children: [_jsx("span", { style: labelStyle, children: "ETA \u00B7 dist \u00B7 fare" }), _jsxs("span", { style: { fontSize: 13, fontWeight: 700, fontVariantNumeric: "tabular-nums" }, children: [d.eta_min.toFixed(0), " min \u00B7 ", d.distance_km.toFixed(1), " km \u00B7 \u20B9", Math.round(d.fare_inr)] })] }), _jsxs("div", { style: rowFlex, children: [_jsx("span", { style: monoBadge, children: d.kyc_ref }), d.booked ? (_jsx("span", { style: bookedBtn, children: "Booked \u00B7 en route" })) : (_jsxs("button", { style: bookBtn, type: "button", onClick: () => handleBook(d.id), "data-testid": `carpool-book-${d.id}`, children: ["Book \u00B7 \u00D7", d.surge.toFixed(2)] }))] })] }, d.id))), confirmation ? (_jsxs("div", { style: {
                    fontSize: 12,
                    padding: "8px 10px",
                    background: "#dff0e6",
                    border: "1px solid #a9d3bd",
                    color: "#0f4d3a",
                    borderRadius: 4,
                }, children: ["Confirmed \u00B7 ride ", confirmation, " \u00B7 driver en route"] })) : null, _jsx("button", { type: "button", style: sosBtn, onClick: () => window.alert("SOS routed to operator incident channel (simulated)."), "data-testid": "carpool-sos", children: "SOS \u00B7 alert operator" }), _jsx("div", { style: {
                    fontSize: 11,
                    color: "var(--color-cool-ash)",
                    lineHeight: 1.5,
                    paddingTop: 4,
                    borderTop: "1px dashed var(--color-pebble)",
                }, children: "Polylines follow real OSM road geometry via OSRM public server. When a Mumbai/Delhi Beckn BPP goes live, we flip a flag; the trust primitives, surge cap, and SOS surface don't change." })] }));
}
